import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useStore } from 'zustand';
import { kurvFormSlice } from 'app/store/kurvFormSlice';
import { IKurvEquipmentFormValues, kurvEquipmentSchema } from './validation';
import { IOffersItem } from 'src/app/types';
import { useLeadByIdQuery } from 'src/app/queries/leads.queries';
import FuseLoading from '@fuse/core/FuseLoading';

interface IProps {
  index: number;
  setNextForm: () => void;
  setPrevForm: () => void;
  offer: IOffersItem;
}

const KurvEquipmentForm = ({ setNextForm, setPrevForm, index, offer }: IProps) => {
  const store = useStore(kurvFormSlice);
  const { data: currentLead, isLoading } = useLeadByIdQuery(offer.leadId || 0, {
    enabled: !!offer.leadId,
  });

  const methods = useForm<IKurvEquipmentFormValues>({
    mode: 'onChange',
    values: store.equipment,
    resolver: yupResolver(kurvEquipmentSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const isRetail = currentLead?.data.domain.includes('retail');
  const isECommerce = currentLead?.data.domain.includes('ecommerce');

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col flex-1 justify-between mt-16"
        onSubmit={handleSubmit((d) => {
          store.setEquipment(d);
          setNextForm();
        })}
      >
        {isLoading && (
          <div className="fixed inset-0 flex bg-[#0000008a] z-50 [&>div>p]:text-black">
            <FuseLoading />
          </div>
        )}

        <div>
          {isRetail && (
            <>
              <Typography className="font-500 tracking-tight leading-tight mb-[20px]">
                Credit Card (Retail):
              </Typography>
              <div className="flex flex-col">
                <FormControlLabel
                  control={
                    <Controller
                      name="retailKurvMobileB200"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Kurv Mobile (B200 reader w/chip reader)"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="retailKurvMobileB250"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Kurv Mobile (B250 reader w/chip and tap)"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="retailDejavooZ8"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Dejavoo Z8"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="retailDejavooZ9"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Dejavoo Z9 (wireless)"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="retailIHaveMyOwn"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="I have my own"
                />
              </div>
            </>
          )}
          {isECommerce && (
            <>
              <Typography className="font-500 tracking-tight leading-tight">
                Online Gateway (E-Commerce):
              </Typography>
              <div className="flex flex-col">
                <FormControlLabel
                  control={
                    <Controller
                      name="eComConvenuePay"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="ConvenuePay"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="eComAuthNet"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Auth.net"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="eComIHaveMyOwn"
                      control={control}
                      render={({ field: props }) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="I have my own"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-[10px]">
          <Button
            variant="text"
            color="secondary"
            className="mt-16 text-[#111827] disabled:text-dark-primary disabled:text-opacity-60"
            aria-label="Back"
            type="button"
            onClick={setPrevForm}
            size="large"
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className="mt-16 text-white bg-blue-primary disabled:text-dark-primary disabled:bg-gray-400 disabled:text-opacity-60"
            aria-label="Next step"
            type="submit"
            size="large"
            disabled={!isValid}
          >
            Submit
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default KurvEquipmentForm;
